import { Button, Chip, CircularProgress, Stack, TableCell, TableRow, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";

import { OrganizationRoleCompany } from "src/pages/Settings/Users/OrganizationRolesSection/types";

type OrganizationRoleRowProps = {
    company: OrganizationRoleCompany;
    roles: string[];
    isRolesLoading?: boolean;
    canManageRoles: boolean;
    onEdit: (company: OrganizationRoleCompany, roles: string[]) => void;
    onRemove: (company: OrganizationRoleCompany) => void;
};

export const OrganizationRoleRow = (props: OrganizationRoleRowProps) => {
    const { company, roles, isRolesLoading = false, canManageRoles, onEdit, onRemove } = props;
    const { t } = useTranslation();

    const isLoading = isRolesLoading;

    return (
        <TableRow>
            <TableCell>
                <Typography variant="small-medium" sx={{ color: "text.primary", lineHeight: "1.5" }}>
                    {company.name}
                </Typography>
            </TableCell>
            <TableCell>
                {isLoading ?
                    <CircularProgress color="inherit" size={18} />
                : roles.length > 0 ?
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {roles.map((role) => (
                            <Chip
                                key={role}
                                label={role}
                                size="small"
                                sx={{
                                    borderRadius: "6px",
                                    backgroundColor: "action.hover",
                                    color: "text.primary",
                                }}
                            />
                        ))}
                    </Stack>
                :   <Typography variant="small-medium" sx={{ color: "text.disabled" }}>
                        {t("users.organizationRolesNoRoles", "No roles")}
                    </Typography>
                }
            </TableCell>
            {canManageRoles && (
                <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                            type="button"
                            variant="secondary"
                            size="small"
                            disabled={isLoading}
                            startIcon={<EditOutlinedIcon fontSize="small" />}
                            onClick={() => onEdit(company, roles)}
                        >
                            {t("users.organizationRolesEdit", "Edit")}
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            size="small"
                            disabled={isLoading}
                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                            onClick={() => onRemove(company)}
                            sx={{
                                color: "error.main",
                                border: (theme) => `1px solid ${theme.palette.error.light}`,
                                borderRadius: "8px",
                                "&:hover": {
                                    borderColor: "error.main",
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            {t("users.organizationRolesRemove", "Remove")}
                        </Button>
                    </Stack>
                </TableCell>
            )}
        </TableRow>
    );
};
